import React, {
  FunctionComponent,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef
} from 'react'
import EmailInput from './emailInput'
import ChoosePackage from './choosePackage'
import ChooseNumber from './chooseNumber'
import classNames from 'classnames'
import { Button, TextField } from '@mui/material'
import useIntl from '@/utils/intl'
import ChooseSim from './chooseSim'
import ContactInfo from './contactInfo'
import { Obj } from '..'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import { useMobiApi } from '@/services/mobi-api'
import { useToast } from '@/context/ToastContext'
import DarkIconButton from '@/components/ui/Buttons/SecondaryIconButton'
import { IMG_URL } from '@/utils/constants'
import Image from 'next/image'
import {
  ChoosenInfo,
  InstanceNewNumberV2,
  serviceDetail
} from '@/services/mobi-api/numberv2/types'
import { moneyFormatter } from '@/utils/helpers'
import { useTheme } from '@/context/ThemeContext'

interface Props {
  onClick: (objs: Obj) => void
}

interface State {
  checkBtnDisabled?: boolean
  inputRefValue?: string
  isRefCodeValid?: boolean
}

const Step1: FunctionComponent<Props> = ({ onClick }) => {
  const [currentStep, setCurrentStep] = React.useState(0)
  const { translate } = useIntl()
  const [objs, setObj] = React.useState<Obj | undefined>()
  const { numberv2 } = useMobiApi()
  const { showError } = useToast()
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [isContactValid, setContactValid] = React.useState(false)
  const [liveSelectedServices, setLiveSelectedServices] =
    React.useState<ChoosenInfo>({})
  const bottomRef = useRef<HTMLDivElement>(null)
  const [bottomHeight, setBottomHeight] = React.useState(96)
  const [state, setState] = useReducer(
    (state: State, newState: State) => ({ ...state, ...newState }),
    {
      checkBtnDisabled: true,
      inputRefValue: '',
      isRefCodeValid: false
    }
  )
  const { theme } = useTheme()

  useLayoutEffect(() => {
    const updateHeight = () => {
      if (bottomRef.current) {
        setBottomHeight(bottomRef.current.offsetHeight)
      }
    }

    updateHeight()

    const resizeObserver = new ResizeObserver(() => updateHeight())
    if (bottomRef.current) {
      resizeObserver.observe(bottomRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const handleNext = (obj: Obj): void => {
    const mergedObj = {
      ...objs,
      ...obj
    }
    setObj(mergedObj)
    if (currentStep === 4) {
      onClick(mergedObj ?? {})
    } else {
      setCurrentStep(currentStep + 1)
      scrollToNextSection()
    }
  }
  const handleBack = (): void => {
    setCurrentStep(currentStep - 1)
  }

  const handleChangeStep = (step: number): void => {
    setCurrentStep(step)

    if (step === 1) {
      setLiveSelectedServices({})
      setObj((prev) => ({
        ...prev,
        choosenPackage: '',
        choosenInfo: {
          choosenPackage: undefined,
          choosenCallMM: undefined,
          choosenDataMM: undefined,
          choosenSmsMM: undefined,
          choosenVideoStreamingMM: undefined,
          choosenEntertainmentMM: undefined
        }
      }))
    }
  }

  const selectedServices = React.useMemo(() => {
    if (!liveSelectedServices) return []

    return [
      liveSelectedServices?.choosenDataMM,
      liveSelectedServices?.choosenCallMM,
      liveSelectedServices?.choosenSmsMM,
      liveSelectedServices?.choosenVideoStreamingMM,
      ...(liveSelectedServices?.choosenEntertainmentMM ?? [])
    ].filter((s): s is serviceDetail => !!s && !!s.name && !!s.price)
  }, [liveSelectedServices])

  const totalPrice = React.useMemo(() => {
    const packagePrice = liveSelectedServices?.choosenPackage?.price ?? 0
    const servicesTotal = selectedServices.reduce(
      (sum, s) => sum + (s.price ?? 0),
      0
    )
    return Number(packagePrice) + servicesTotal
  }, [selectedServices, liveSelectedServices])

  const selectedPackageName = React.useMemo(() => {
    if (objs?.choosenPackage) {
      return objs?.choosenPackage?.split(' - ')[1] ?? ''
    }
    return ''
  }, [objs?.choosenPackage])

  const handleCheck = async (): Promise<void> => {
    if (state.checkBtnDisabled === true) return
    try {
      setState({ checkBtnDisabled: true })
      const res = await numberv2.checkReferral(
        state.inputRefValue !== undefined ? state.inputRefValue : ''
      )
      if (res?.isValid !== undefined && res?.isValid) {
        if (
          typeof window !== 'undefined' &&
          state.inputRefValue !== 'undefined'
        ) {
          sessionStorage.setItem('mc_refCode', state.inputRefValue ?? '')
        }
        setState({ isRefCodeValid: true })
      } else {
        showError(translate('referralCodeNotValid'))
        setState({ checkBtnDisabled: false })
      }
    } catch (e) {
      showError(translate('error'))
      console.error(e)
    }
  }

  const ConfirmedEmail: FunctionComponent = () => {
    return (
      <div className='bg-surfaceInverse rounded-xl p-4'>
        <TextField
          label={translate('email')}
          variant='outlined'
          className='w-full rounded-lg'
          value={objs?.email}
          disabled
          sx={{
            '& fieldset': {
              borderRadius: '12px',
              borderColor: '#D6D6D6'
            },
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: '#D6D6D6'
              }
            },
            '& .MuiOutlinedInput-input.Mui-disabled': {
              color: '#6E7882',
              WebkitTextFillColor: '#6E7882'
            },
            '& label.Mui-disabled': {
              color: '#6E7882'
            },
            '& label.Mui-focused': {
              color: '#262626'
            },
            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
              {
                display: 'none'
              },
            '& input[type=number]': {
              MozAppearance: 'textfield'
            }
          }}
        />
      </div>
    )
  }

  interface PrevStepProps {
    title: string
    value: string
    index: number
  }
  const prevStep = (props: PrevStepProps): JSX.Element => {
    return (
      <div className='flex justify-between bg-surfaceInverse rounded-xl p-4 '>
        <div className='flex flex-col gap-2'>
          <span className='text-[#6E7882] text-xs'>{props.title}</span>
          <span className='text-[#6E7882] text-sm font-bold truncate max-w-[250px]'>
            {props.value ?? ''}
          </span>
        </div>
        <DarkButton
          type='outlined'
          size='sm'
          onClick={() => handleChangeStep(props.index)}
        >
          {translate('change')}
        </DarkButton>
      </div>
    )
  }
  const steps = [
    {
      label: translate('email.registration'),
      body: <EmailInput next={handleNext} back={handleBack} />,
      past: <ConfirmedEmail />,
      id: 'email'
    },
    {
      label: translate('packageselection'),
      body: (
        <ChoosePackage
          next={handleNext}
          back={handleBack}
          instanceId={objs?.instanceId ?? ''}
          referralCode={state.inputRefValue}
          onSelectionChange={setLiveSelectedServices}
        />
      ),
      past: prevStep({
        title: translate('data'),
        value: objs?.choosenPackage ?? '',
        index: 1
      }),
      id: 'package'
    },
    {
      label: translate('number.choose'),
      body: (
        <ChooseNumber
          next={handleNext}
          instanceId={objs?.instanceId ?? ''}
          rateplan={objs?.rateplan ?? ''}
        />
      ),
      past: prevStep({
        title: translate('number.chosen'),
        value: objs?.choosenNumber ?? '',
        index: 2
      }),
      id: 'number'
    },
    {
      label: translate('sim.choose'),
      body: (
        <ChooseSim
          next={handleNext}
          back={handleBack}
          instanceId={objs?.instanceId ?? ''}
        />
      ),
      past: prevStep({
        title: translate('sim.choosen'),
        value: objs?.choosenSim ?? '',
        index: 3
      }),
      id: 'sim'
    },
    {
      label: translate('stars.contact'),
      body: (
        <ContactInfo
          next={handleNext}
          back={handleBack}
          onValidationChange={(valid) => setContactValid(valid)}
          instanceId={objs?.instanceId ?? ''}
        />
      ),
      past: <div />,
      id: 'contact'
    }
  ]

  const scrollToNextSection = (): void => {
    const sectionElement = document.getElementById(steps[currentStep].id)
    const nextSectionElement = document.getElementById(
      steps[currentStep + 1].id
    )
    if (sectionElement != null && nextSectionElement != null) {
      sectionElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      })
    }
  }

  return (
    <div
      className='w-full xl:w-1/2 lg:w-3/4 md:w-4/5 h-full flex flex-col justify-center p-3 md:p-0'
      style={{ marginBottom: bottomHeight }}
    >
      <div className='flex bg-surfaceMid rounded-2xl p-2 m-0'>
        <div className='flex w-full bg-surfaceInverse border border-secondarySoftLow rounded-xl items-center'>
          <TextField
            label={translate('referralPromoCode')}
            className='flex-1 rounded-l-xl border-secondaryHighStroke bg-surfaceInverse text-textMid'
            value={state.inputRefValue}
            onChange={(e) => {
              const inputValue = e.target.value
              if (/^[a-zA-Z0-9]*$/.test(inputValue)) {
                if (inputValue.length === 0) {
                  setState({ checkBtnDisabled: true })
                } else {
                  setState({ checkBtnDisabled: false })
                }
                setState({ inputRefValue: inputValue })
              }
            }}
            InputProps={{
              readOnly: state.isRefCodeValid,
              inputProps: {
                maxLength: 8
              },
              style: {
                color: 'var(--color-text-textHigh)',
                '&::placeholder': {
                  color: 'var(--color-text-secondaryHighStroke)'
                }
              } as any
            }}
            InputLabelProps={{
              style: { color: 'var(--color-text-textHigh)' }
            }}
            sx={{
              '& fieldset': {
                border: 'none'
              },
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& label.Mui-focused': {
                color: '#262626'
              },
              '& input': {
                border: 'none',
                outline: 'none',
                color: 'textHigh'
              },
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                {
                  display: 'none'
                },
              '& input[type="text"]': {
                MozAppearance: 'textfield'
              }
            }}
          />
          {state.isRefCodeValid === false ? (
            <DarkButton
              classNames='w-30 text-textInverse text-base font-bold m-2'
              onClick={() => {
                void handleCheck()
              }}
              disabled={state.checkBtnDisabled}
            >
              {translate('referralCodeActivate')}
            </DarkButton>
          ) : (
            <Button
              className='m-2'
              disabled
              sx={{
                borderRadius: '12px',
                padding: '0 16px',
                textTransform: 'none'
              }}
            >
              <img
                src='/icons/verified.svg'
                height={20}
                width={20}
                alt='info'
              />
            </Button>
          )}
        </div>
      </div>
      {steps.map((step, index) => (
        <div
          key={index}
          id={step.id}
          className='flex flex-col gap-4 my-4 transition-all duration-500'
        >
          <span
            className={classNames(
              currentStep !== index
                ? 'text-[#6E7882] font-bold'
                : 'font-extrabold text-textHigh'
            )}
          >
            {index + 1}. {step.label}
          </span>
          {currentStep > index ? (
            <div>{step.past}</div>
          ) : (
            <div
              className={classNames(
                currentStep < index ? 'opacity-50 pointer-events-none' : ''
              )}
            >
              {typeof window !== 'undefined' ? step.body : null}
            </div>
            // step.body
          )}
        </div>
      ))}
      <div
        ref={bottomRef}
        className='flex-col fixed md:sticky bottom-0 left-0 w-full bg-surfaceLow z-50 p-4 pt-0 gap-1 shadow-[0px_-2px_8px_0px_rgba(0,0,0,0.08)] rounded-t-xl'
      >
        <div
          className='flex justify-between items-center h-12'
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className='flex items-center gap-2'>
            <div>
              <Image
                width={18}
                height={18}
                alt='img'
                src={
                  theme === 'dark'
                    ? isExpanded
                      ? IMG_URL + '/uploads/icons/chevron-down-white.svg'
                      : IMG_URL + '/uploads/icons/chevron-up-white.svg'
                    : isExpanded
                    ? IMG_URL + '/uploads/icons/chevron-down.svg'
                    : IMG_URL + '/uploads/icons/chevron-up.svg'
                }
              />
            </div>
            <p className='text-secondary text-sm font-bold'>
              {translate('package.viewDetails')}
            </p>
          </div>
          <p className='text-base font-bold text-textHigh'>
            {moneyFormatter(totalPrice)}
          </p>
        </div>
        {isExpanded && (
          <div className='py-2 space-y-1 text-sm text-gray-600'>
            {selectedServices.map((item, index) => (
              <div key={index} className='flex items-center justify-between'>
                <p className='text-sm text-textMid font-medium truncate max-w-[65%]'>
                  {item.name}
                </p>
                <p className='text-sm text-textMid font-medium'>
                  {moneyFormatter(item.price)}
                </p>
              </div>
            ))}
          </div>
        )}
        <DarkButton
          fullwidth
          disabled={!isContactValid}
          onClick={() => {
            document
              .querySelector('#contact-continue')
              ?.dispatchEvent(new Event('click', { bubbles: true }))
          }}
        >
          {translate('continue')}
        </DarkButton>
      </div>
    </div>
    // <div className='w-full h-48 bg-red-100' />
  )
}

export default Step1
